/**
 * Riga «Assenze/Desiderata» nel piè della griglia Piano Turni: modello dei chip di un giorno.
 *
 * Le sigle sono quelle della pagina Assenze (ABSENCE_CATEGORY_ABBR); le lettere delle fasce
 * si aggiungono solo quando l'assenza è parziale, quindi una sigla nuda vale «tutto il giorno»
 * (`F` = formazione intera, `F P` = solo pomeriggio). I desiderata mostrano le sole fasce
 * rifiutate, senza `NO`: il colore del chip dice che sono desiderata.
 *
 * Le chiavi in `itemKeys` sono le stesse di `collectSelectionContent` in AbsencesCalendar,
 * così il popup di modifica sa quale voce evidenziare.
 */
import { getState, getEffectiveAbsences } from './store.js';
import {
  ABSENCE_CATEGORIES,
  ABSENCE_CATEGORY_ABBR,
  ABSENCE_CATEGORY_LABELS,
  SLOT_ABSENCE_LABELS,
  DESIDERATA_TYPES,
  DESIDERATA_LABELS,
} from './types.js';

/** Lettere delle fasce negli apici, nell'ordine in cui vengono concatenate. */
const SLOT_LETTERS = { mattino: 'M', pomeriggio: 'P', prolungato: 'Pr', notte: 'N' };
const SLOT_LETTER_ORDER = ['mattino', 'pomeriggio', 'prolungato', 'notte'];

/**
 * Fasce che valgono «giornata intera» per le assenze a slot: per la libera professione
 * sono tutte e quattro, per la formazione mattino+pomeriggio (ciò che il modale salva
 * scegliendo «Intera giornata», 7,6h).
 */
const FULL_DAY_SLOTS = {
  [ABSENCE_CATEGORIES.LIBERA_PROFESSIONE]: ['mattino', 'pomeriggio', 'prolungato', 'notte'],
  [ABSENCE_CATEGORIES.FORMAZIONE]: ['mattino', 'pomeriggio'],
};

const SLOT_ABSENCE_CATEGORIES = [ABSENCE_CATEGORIES.LIBERA_PROFESSIONE, ABSENCE_CATEGORIES.FORMAZIONE];

/** Fasce bloccate da ogni tipo di desiderata (le no-reperibilità non bloccano una fascia). */
const DESIDERATA_BAND_LETTERS = {
  [DESIDERATA_TYPES.NO_MATTINO]: ['M'],
  [DESIDERATA_TYPES.NO_POMERIGGIO]: ['P'],
  [DESIDERATA_TYPES.NO_POMERIGGIO_NOTTE]: ['P', 'N'],
  [DESIDERATA_TYPES.NO_NOTTE]: ['N'],
};
const DESIDERATA_REP_TOKENS = {
  [DESIDERATA_TYPES.NO_REPERIBILITA_GIORNO]: 'RG',
  [DESIDERATA_TYPES.NO_REPERIBILITA_NOTTE]: 'RN',
};

const BAND_LETTER_ORDER = ['M', 'P', 'Pr', 'N'];
const REP_TOKEN_ORDER = ['RG', 'RN'];

/** Ordine dei chip dentro la cella: prima chi non c'è tutto il giorno. */
const KIND_RANK = { absence: 0, lp: 1, formazione: 2, desiderata: 3 };

/** Token «LP» finito tra i desiderata da una vecchia versione: blocca il diurno, non è una categoria. */
function isLegacyLpDesiderataToken(raw) {
  const token = String(raw || '').trim().toLowerCase();
  if (!token) return false;
  const compact = token.replace(/[^a-z0-9]+/g, '');
  return compact === 'lp' || compact.startsWith('lp') || compact.includes('liberaprofessione');
}

function isRecurringAbsence(a) {
  return typeof a?.id === 'string' && a.id.startsWith('recurring:');
}

function absenceItemKey(a) {
  if (isRecurringAbsence(a)) {
    return `rec:${a._recurringId || String(a.id).split(':')[1]}`;
  }
  return `abs:${a.id}`;
}

function coversDate(a, dateStr) {
  const start = (a.dateStart || '').slice(0, 10);
  const end = (a.dateEnd || a.dateStart || '').slice(0, 10) || start;
  if (!start) return false;
  return dateStr >= start && dateStr <= end;
}

/** Lettere delle fasce concatenate senza separatori, nell'ordine M · P · Pr · N. */
function slotLettersApex(slotTypes) {
  const set = new Set(slotTypes || []);
  return SLOT_LETTER_ORDER.filter(s => set.has(s)).map(s => SLOT_LETTERS[s]).join('');
}

function coversFullDay(category, slotTypes) {
  const needed = FULL_DAY_SLOTS[category];
  if (!needed) return false;
  const set = new Set(slotTypes || []);
  return needed.every(s => set.has(s));
}

function slotTypesLabel(slotTypes) {
  return (slotTypes || []).map(s => SLOT_ABSENCE_LABELS[s] || s).join(', ');
}

/** Apice di un desiderata: fasce concatenate, poi le no-reperibilità separate da spazio (`N RG`). */
function desiderataApex(types) {
  const bands = new Set();
  const reps = new Set();
  for (const t of types || []) {
    if (DESIDERATA_REP_TOKENS[t]) {
      reps.add(DESIDERATA_REP_TOKENS[t]);
      continue;
    }
    if (isLegacyLpDesiderataToken(t)) {
      ['M', 'P', 'Pr'].forEach(L => bands.add(L));
      reps.add('RG');
      continue;
    }
    for (const L of DESIDERATA_BAND_LETTERS[t] || []) bands.add(L);
  }
  const parts = [];
  const bandApex = BAND_LETTER_ORDER.filter(L => bands.has(L)).join('');
  if (bandApex) parts.push(bandApex);
  REP_TOKEN_ORDER.filter(t => reps.has(t)).forEach(t => parts.push(t));
  return parts.join(' ');
}

function desiderataTitle(types) {
  const labels = (types || [])
    .filter(t => !isLegacyLpDesiderataToken(t))
    .map(t => DESIDERATA_LABELS[t] || t);
  const legacy = (types || []).some(isLegacyLpDesiderataToken);
  if (legacy) labels.push('Libera professione registrata come desiderata (diurno)');
  return labels.join(' · ');
}

function doctorLabel(doc) {
  const nome = [doc?.cognome, doc?.nome].filter(Boolean).join(' ').trim();
  return `${doc?.sigla || ''}${nome ? ` ${nome}` : ''}`.trim();
}

/**
 * Chip di un giorno per i medici indicati.
 * Un chip per medico e categoria: più record della stessa categoria nello stesso giorno
 * si fondono unendo le fasce. Se il medico ha un'assenza a giornata intera, le sue voci
 * a fascia e i suoi desiderata di quel giorno non vengono mostrati: è fuori comunque.
 */
function buildChipsForDate(dateStr, doctors, absences, desiderataByDoctor) {
  const byDoctor = new Map();
  const ensure = (doc) => {
    let entry = byDoctor.get(doc.id);
    if (!entry) {
      entry = { doc, fullDay: new Map(), slotBased: new Map(), desiderata: null };
      byDoctor.set(doc.id, entry);
    }
    return entry;
  };
  const doctorsById = new Map(doctors.map(d => [String(d.id), d]));

  for (const a of absences) {
    const doc = doctorsById.get(String(a.doctorId));
    if (!doc || !coversDate(a, dateStr)) continue;
    const entry = ensure(doc);
    const key = absenceItemKey(a);
    if (SLOT_ABSENCE_CATEGORIES.includes(a.category)) {
      const cur = entry.slotBased.get(a.category) || { slotTypes: new Set(), itemKeys: new Set() };
      (a.slotTypes || []).forEach(s => cur.slotTypes.add(s));
      cur.itemKeys.add(key);
      entry.slotBased.set(a.category, cur);
      continue;
    }
    const cur = entry.fullDay.get(a.category) || { itemKeys: new Set() };
    cur.itemKeys.add(key);
    entry.fullDay.set(a.category, cur);
  }

  for (const [doctorId, types] of desiderataByDoctor) {
    const doc = doctorsById.get(String(doctorId));
    if (!doc || !types?.length) continue;
    ensure(doc).desiderata = types;
  }

  const chips = [];
  for (const entry of byDoctor.values()) {
    const doc = entry.doc;
    const who = doctorLabel(doc);
    if (entry.fullDay.size > 0) {
      for (const [category, data] of entry.fullDay) {
        chips.push({
          doctorId: doc.id,
          sigla: doc.sigla || '',
          kind: 'absence',
          apex: ABSENCE_CATEGORY_ABBR[category] || '',
          title: `${who} — ${ABSENCE_CATEGORY_LABELS[category] || category} (tutto il giorno)`,
          itemKeys: [...data.itemKeys],
        });
      }
      continue;
    }
    for (const [category, data] of entry.slotBased) {
      const slotTypes = [...data.slotTypes];
      const full = coversFullDay(category, slotTypes);
      const isFormazione = category === ABSENCE_CATEGORIES.FORMAZIONE;
      // La libera professione porta solo le lettere delle fasce: il colore del chip dice già
      // di che assenza si tratta, e senza lettere vale per l'intera giornata.
      const abbr = isFormazione ? (ABSENCE_CATEGORY_ABBR[category] || '') : '';
      const letters = full ? '' : slotLettersApex(slotTypes);
      const detail = full ? 'tutto il giorno' : (slotTypesLabel(slotTypes) || 'fasce non indicate');
      chips.push({
        doctorId: doc.id,
        sigla: doc.sigla || '',
        kind: isFormazione ? 'formazione' : 'lp',
        apex: [abbr, letters].filter(Boolean).join(' '),
        title: `${who} — ${ABSENCE_CATEGORY_LABELS[category] || category}: ${detail}`,
        itemKeys: [...data.itemKeys],
      });
    }
    if (entry.desiderata?.length) {
      const apex = desiderataApex(entry.desiderata);
      if (apex) {
        chips.push({
          doctorId: doc.id,
          sigla: doc.sigla || '',
          kind: 'desiderata',
          apex,
          title: `${who} — Desiderata: ${desiderataTitle(entry.desiderata)}`,
          itemKeys: entry.desiderata.map(t => `des:${t}`),
        });
      }
    }
  }

  chips.sort((a, b) => {
    const rank = KIND_RANK[a.kind] - KIND_RANK[b.kind];
    if (rank !== 0) return rank;
    const bySigla = String(a.sigla).localeCompare(String(b.sigla), 'it', { sensitivity: 'base' });
    if (bySigla !== 0) return bySigla;
    return String(a.apex).localeCompare(String(b.apex), 'it', { sensitivity: 'base' });
  });
  return chips;
}

/**
 * Chip della riga «Assenze/Desiderata» per un intervallo di giorni: `Map(date → chips[])`.
 * Una sola lettura di assenze e desiderata per tutto l'intervallo, non una per cella:
 * questa funzione gira a ogni re-render della griglia.
 */
export function buildPlannerAbsenceChipsByDate(dates, doctors) {
  const out = new Map();
  const days = (dates || []).filter(Boolean);
  if (days.length === 0 || !doctors?.length) return out;
  const sorted = [...days].sort();
  const rangeStart = sorted[0];
  const rangeEnd = sorted[sorted.length - 1];
  const absences = getEffectiveAbsences(rangeStart, rangeEnd);
  const desiderata = (getState().desiderata || []).filter(d => {
    const date = (d?.date || '').slice(0, 10);
    return date >= rangeStart && date <= rangeEnd;
  });
  const desiderataByDate = new Map();
  for (const d of desiderata) {
    const date = (d.date || '').slice(0, 10);
    if (!desiderataByDate.has(date)) desiderataByDate.set(date, new Map());
    const prev = desiderataByDate.get(date).get(String(d.doctorId)) || [];
    desiderataByDate.get(date).set(String(d.doctorId), [...prev, ...(d.types || [])]);
  }
  for (const date of days) {
    out.set(date, buildChipsForDate(date, doctors, absences, desiderataByDate.get(date) || new Map()));
  }
  return out;
}
